/*
  # Docket Autocomplete Hook

  Custom hook for handling docket number autocomplete functionality
  with debouncing and suggestion management.
*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { docketSuggestionsService, DocketSuggestion } from '../lib/docketSuggestionsService';

interface UseDocketAutocompleteOptions {
  debounceMs?: number;
  minQueryLength?: number;
  maxSuggestions?: number;
}

interface UseDocketAutocompleteReturn {
  suggestions: DocketSuggestion[];
  isLoading: boolean;
  error: string | null;
  showSuggestions: boolean;
  selectedIndex: number;
  setQuery: (query: string) => void;
  selectSuggestion: (index: number) => string | null;
  clearSuggestions: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => string | null;
  setShowSuggestions: (show: boolean) => void;
}

export const useDocketAutocomplete = (
  options: UseDocketAutocompleteOptions = {}
): UseDocketAutocompleteReturn => {
  const {
    debounceMs = 300,
    minQueryLength = 2,
    maxSuggestions = 10
  } = options;

  const [suggestions, setSuggestions] = useState<DocketSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [query, setQueryState] = useState('');

  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  // Debounced search function
  const searchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      const result = await docketSuggestionsService.getSuggestions(searchQuery, maxSuggestions);
      
      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      setSuggestions(result.suggestions);
      setShowSuggestions(result.suggestions.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      
      console.error('Error fetching docket suggestions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch suggestions');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, [minQueryLength, maxSuggestions]);

  // Set query with debouncing
  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery);
    
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      searchSuggestions(newQuery);
    }, debounceMs);
  }, [searchSuggestions, debounceMs]);

  // Select a suggestion by index
  const selectSuggestion = useCallback((index: number): string | null => {
    if (index >= 0 && index < suggestions.length) {
      const selectedSuggestion = suggestions[index];
      setQueryState(selectedSuggestion.docketNumber);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      return selectedSuggestion.docketNumber;
    }
    return null;
  }, [suggestions]);

  // Clear suggestions
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setError(null);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent): string | null => {
    if (!showSuggestions || suggestions.length === 0) {
      return null;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        return null;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        return null;

      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          return selectSuggestion(selectedIndex);
        }
        return null;

      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        return null;

      default:
        return null;
    }
  }, [showSuggestions, suggestions, selectedIndex, selectSuggestion]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    showSuggestions,
    selectedIndex,
    setQuery,
    selectSuggestion,
    clearSuggestions,
    handleKeyDown,
    setShowSuggestions
  };
};
